import React from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import { useModelStore } from "../../../../store/useModelStore";
import { useUIStore } from "../../../../store/useUIStore";
// import { VIEW_CONFIGURATIONS } from '../../utils/workspace.constants';
import { throttle } from "lodash";

interface Props {
  currentView: "front" | "top" | "side" | "isometric";
  enableSnap?: boolean;
}

// Small helper to project a 3D position to screen pixels
function toScreenPosition(
  pos: THREE.Vector3,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement
) {
  const proj = pos.clone().project(camera);
  const x = ((proj.x + 1) / 2) * canvas.clientWidth;
  const y = ((-proj.y + 1) / 2) * canvas.clientHeight;
  return { x, y };
}

export const NodeDragHandler: React.FC<Props> = ({
  currentView,
  enableSnap = true,
}) => {
  const { gl, camera } = useThree();
  const canvas = gl.domElement as HTMLCanvasElement;
  // Note: Don't subscribe nodes into this component; we'll read fresh state via getState() inside handlers
  const updateNode = useModelStore((state) => state.updateNode);
  const regenerateBeams = useModelStore((state) => state.regenerateBeams);
  const clearSelection = useModelStore((state) => state.clearSelection);
  const selectNode = useModelStore((state) => state.selectNode);
  const isPlanar = currentView !== "isometric";

  const raycasterRef = React.useRef(new THREE.Raycaster());
  const draggingRef = React.useRef(false);
  const selectedNodeRef = React.useRef<string | null>(null);
  const pointerStartRef = React.useRef<{ x: number; y: number } | null>(null);

  const tooltipRef = React.useRef<HTMLDivElement | null>(null);

  // Throttled updater for performance
  const throttledUpdate = React.useMemo(
    () =>
      throttle((id: string, x: number, y: number, z: number) => {
        updateNode(id, { x, y, z });
        regenerateBeams(id);
      }, 16),
    [updateNode, regenerateBeams]
  );

  React.useEffect(() => {
    if (!canvas || !isPlanar) return;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return; // only left click
      if (!e.ctrlKey) return; // require Ctrl

      // compute ndc
      const rect = canvas.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );

      // raycast against node positions (treated as small spheres)
      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(ndc, camera);

      // Build list of intersects by projecting each node to world position
      const intersects: { id: string; point: THREE.Vector3; dist: number }[] =
        [];
      const currentNodes = (useModelStore as any).getState().nodes as Array<{
        id: string;
        x: number;
        y: number;
        z: number;
      }>;
      for (const node of currentNodes) {
        const pos = new THREE.Vector3(node.x, node.y, node.z);
        const toPoint = new THREE.Vector3().subVectors(
          pos,
          raycaster.ray.origin
        );
        const proj = raycaster.ray.direction
          .clone()
          .multiplyScalar(raycaster.ray.direction.clone().dot(toPoint));
        const closest = proj.add(raycaster.ray.origin);
        const dist = closest.distanceTo(pos);
        // tolerance in world units (use small value or based on camera distance)
        // smaller tolerance to avoid accidental hits while panning
        const tolerance = 0.25; // tuned for typical scene scale
        if (dist <= tolerance) {
          const dAlong = raycaster.ray.origin.distanceTo(closest);
          intersects.push({ id: node.id, point: pos, dist: dAlong });
        }
      }

      if (intersects.length === 0) {
        // No node under cursor; do nothing further but keep controls blocked for this gesture
        return;
      }

      // Always block OrbitControls on Ctrl+Left in planar views (even if no node picked)
      e.stopPropagation();
      e.preventDefault();

      // pick nearest
      intersects.sort((a, b) => a.dist - b.dist);
      const picked = intersects[0];
      selectedNodeRef.current = picked.id;
      draggingRef.current = true;
      pointerStartRef.current = { x: e.clientX, y: e.clientY };

      // Highlight/select the node
      try {
        clearSelection && clearSelection();
        selectNode && selectNode(picked.id);
      } catch {}

      // Show tooltip
      const tooltip = document.createElement("div");
      tooltip.style.position = "fixed";
      tooltip.style.background = "rgba(0,0,0,0.8)";
      tooltip.style.color = "white";
      tooltip.style.padding = "6px 8px";
      tooltip.style.borderRadius = "4px";
      tooltip.style.fontSize = "12px";
      tooltip.style.pointerEvents = "none";
      tooltip.id = "node-drag-tooltip";
      document.body.appendChild(tooltip);
      tooltipRef.current = tooltip;
      updateTooltipPosition(picked.id);

      // capture the pointer on the canvas so move/up events are delivered to us
      try {
        (canvas as Element).setPointerCapture((e as any).pointerId);
      } catch {
        /* noop */
      }

      // disable camera controls while dragging - dispatch a custom event
      const ev = new CustomEvent("disable-camera-controls", {
        detail: { disable: true },
      });
      window.dispatchEvent(ev);

      // Prevent OrbitControls and other listeners from also handling this pointer
      e.stopPropagation();
      e.preventDefault();
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!draggingRef.current || !selectedNodeRef.current) return;
      // convert pointer to plane coordinates depending on view
      const rect = canvas.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(ndc, camera);

      // intersect with ground plane per view
      let plane: THREE.Plane;
      if (currentView === "top") {
        plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0); // XZ plane
      } else if (currentView === "front") {
        plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0); // XY plane projection, Z fixed
      } else {
        // side
        plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), 0); // YZ plane, X fixed
      }

      const intersect = new THREE.Vector3();
      const ok = raycaster.ray.intersectPlane(plane, intersect);
      if (!ok) return;

      // map intersection to node coordinates depending on view
      let nx = intersect.x,
        ny = intersect.y,
        nz = intersect.z;
      const old = (useModelStore as any)
        .getState()
        .nodes.find((n: any) => n.id === (selectedNodeRef.current as string));
      if (!old) return;

      if (currentView === "front") {
        // Front: X,Y plane, Z fixed
        nz = old.z;
      } else if (currentView === "top") {
        // Top: X,Z plane, Y fixed
        ny = old.y;
      } else if (currentView === "side") {
        // Side: Y,Z plane, X fixed
        nx = old.x;
      }

      // Snapping (read from UI store safely)
      try {
        const ui = (useUIStore as any).getState?.();
        const snapEnabled = (ui?.snapEnabled ?? enableSnap) as boolean;
        const snap = (ui?.gridSize ?? 1) as number;
        if (snapEnabled && snap > 0) {
          nx = Math.round(nx / snap) * snap;
          ny = Math.round(ny / snap) * snap;
          nz = Math.round(nz / snap) * snap;
        }
      } catch {
        // ignore snapping if UI store unavailable
      }

      // Throttled update
      throttledUpdate(selectedNodeRef.current as string, nx, ny, nz);
      updateTooltipPosition(selectedNodeRef.current as string);

      // Prevent default/pan while dragging
      e.stopPropagation();
      e.preventDefault();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      selectedNodeRef.current = null;
      pointerStartRef.current = null;

      // remove tooltip
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }

      // re-enable camera controls
      const ev = new CustomEvent("disable-camera-controls", {
        detail: { disable: false },
      });
      window.dispatchEvent(ev);

      try {
        (canvas as Element).releasePointerCapture((e as any).pointerId);
      } catch {
        /* noop */
      }

      e.stopPropagation();
      e.preventDefault();
    };

    const updateTooltipPosition = (nodeId: string) => {
      const tooltip = tooltipRef.current;
      if (!tooltip) return;
      const node = (useModelStore as any)
        .getState()
        .nodes.find((n: any) => n.id === nodeId);
      if (!node) return;
      const pos = new THREE.Vector3(node.x, node.y, node.z);
      const screen = toScreenPosition(pos, camera, canvas);
      // clamp to viewport
      const pad = 8;
      const x = Math.min(window.innerWidth - 120, Math.max(pad, screen.x + 12));
      const y = Math.min(window.innerHeight - 40, Math.max(pad, screen.y - 20));
      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y}px`;
      const fmt = (v: number) => Number(v).toFixed(2);
      tooltip.textContent = `ID:${node.id}  X:${fmt(node.x)} Y:${fmt(node.y)} Z:${fmt(node.z)}`;
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      throttledUpdate.cancel();
    };
  }, [canvas, camera, currentView, isPlanar, throttledUpdate]);

  return null;
};

export default NodeDragHandler;
